import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Grid,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import color from "../../../constants/color";

const BpIcon = styled("span")(() => ({
  borderRadius: "50%",
  width: 16,
  height: 16,
  backgroundColor: "#D9D9D9",
  "input:hover ~ &": { backgroundColor: "#D9D9D9" },
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: "#28AA4A",
  backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
  "&::before": {
    display: "block",
    width: 16,
    height: 16,
    backgroundImage: "radial-gradient(#fff,#fff 28%,transparent 32%)",
    content: "''",
  },
  "input:hover ~ &": { backgroundColor: "#28AA4A" },
});

function BpRadio(props) {
  return (
    <Radio
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      {...props}
    />
  );
}

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": { width: 15 },
    "& .MuiSwitch-switchBase.Mui-checked": { transform: "translateX(9px)" },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": { opacity: 1, backgroundColor: "#28AA4A" },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], { duration: 200 }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,.35)"
        : "rgba(0,0,0,.25)",
    boxSizing: "border-box",
  },
}));

const SubAdminPermissionsComponent = ({
  selectedCountries = [],
  assignedCompanies = [],
  companiesList = [],
  countryPermissions = {},
  onPermissionsChange = () => { },
  onSave = () => { },
  onCancel = () => { },
  loading = false,
  hasChanges = false,
}) => {
  const modulesList = [
    { key: "subscriptions", label: "Subscriptions" },
    { key: "financialYear", label: "Financial Year" },
    { key: "taxTable", label: "Tax Table" },
    { key: "threshold", label: "Threshold" },
    { key: "rebate", label: "Rebate" },
    { key: "credits", label: "Credits" },
    { key: "allowances", label: "Allowances" },
    { key: "leaves", label: "Leaves" },
    { key: "taxContribution", label: "Tax Contribution" },
  ];

  const [permissions, setPermissions] = useState({});
  const [moduleToggles, setModuleToggles] = useState({});

  const effectiveKey = "General";

  // Initialize permissions from existing data (support old country-based + new general)
  useEffect(() => {
    let initialPerms = {};

    if (countryPermissions && Object.keys(countryPermissions).length > 0) {
      const firstKey = Object.keys(countryPermissions)[0];

      // Handle both old (country name) and new ("General") structure
      initialPerms = {
        [effectiveKey]: countryPermissions[firstKey] || countryPermissions[effectiveKey] || {}
      };
    } else {
      initialPerms = { [effectiveKey]: {} };
    }

    setPermissions(initialPerms);

    // Initialize toggles
    const toggles = { [effectiveKey]: {} };
    modulesList.forEach((module) => {
      const mod = initialPerms[effectiveKey]?.[module.key];
      const hasAccess = mod?.view === true || mod?.edit === true;
      toggles[effectiveKey][module.key] = hasAccess;
    });

    setModuleToggles(toggles);
  }, [countryPermissions, modulesList]);

  const handleToggle = (moduleKey) => {
    const currentValue = moduleToggles[effectiveKey]?.[moduleKey] || false;

    setModuleToggles((prev) => ({
      ...prev,
      [effectiveKey]: {
        ...prev[effectiveKey],
        [moduleKey]: !currentValue,
      },
    }));

    const updatedPermissions = { ...permissions };
    if (!updatedPermissions[effectiveKey]) updatedPermissions[effectiveKey] = {};

    if (!currentValue) {
      // Turning ON
      updatedPermissions[effectiveKey][moduleKey] = { view: true, edit: false };
    } else {
      // Turning OFF
      updatedPermissions[effectiveKey][moduleKey] = { view: false, edit: false };
    }

    setPermissions(updatedPermissions);
    onPermissionsChange(updatedPermissions);
  };

  const handlePermissionChange = (moduleKey, value) => {
    const updatedPermissions = { ...permissions };
    if (!updatedPermissions[effectiveKey]) updatedPermissions[effectiveKey] = {};

    if (value === "view") {
      updatedPermissions[effectiveKey][moduleKey] = { view: true, edit: false };
    } else if (value === "edit") {
      updatedPermissions[effectiveKey][moduleKey] = { view: true, edit: true };
    }

    setPermissions(updatedPermissions);
    onPermissionsChange(updatedPermissions);
  };

  const getPermissionValue = (moduleKey) => {
    const modulePerms = permissions?.[effectiveKey]?.[moduleKey];
    if (modulePerms?.edit) return "edit";
    if (modulePerms?.view) return "view";
    return "view";
  };

  const handleRestoreDefault = () => {
    const defaultPermissions = { [effectiveKey]: {} };
    modulesList.forEach((module) => {
      defaultPermissions[effectiveKey][module.key] = { view: true, edit: false };
    });

    setPermissions(defaultPermissions);
    onPermissionsChange(defaultPermissions);

    const toggles = { [effectiveKey]: {} };
    modulesList.forEach((module) => {
      toggles[effectiveKey][module.key] = true;
    });
    setModuleToggles(toggles);
  };

  return (
    <Box>
      {selectedCountries?.length > 0 && (
        <>
          <Box sx={{ display: "flex", alignItems: "end", justifyContent: "space-between", mb: 3, mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ border: "1.5px solid #28AA4A", width: "20px", mr: 1 }} />
              <Typography sx={{ fontSize: "14px", fontWeight: 500, color: color.text }}>
                Permissions
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={handleRestoreDefault}
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                backgroundColor: "#28AA4A",
                fontSize: "14px",
                fontWeight: 500,
                px: 4,
                "&:hover": { backgroundColor: "#28AA4A" },
              }}
            >
              Restore to Default
            </Button>
          </Box>


          <Box sx={{ mb: 4 }}>
            <Box sx={{ mt: 3, mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: color.primary,
                  width: "fit-content",
                  borderBottom: "1px solid #28AA4A",
                  pb: 2,
                }}
              >
                General Permissions
              </Typography>
            </Box>

            <Grid container sx={{ mb: 3 }}>
              {modulesList.map((module) => (
                <Grid item xs={12} lg={5} key={module.key}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      mt: 4,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: 500, color: color.text }}>
                        {module.label}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: 400, color: color.lightText, mt: 1 }}>
                        Manage {module.label}
                      </Typography>

                      {moduleToggles[effectiveKey]?.[module.key] && (
                        <Box sx={{ mt: 1.5 }}>
                          <RadioGroup
                            row
                            value={getPermissionValue(module.key)}
                            onChange={(e) => handlePermissionChange(module.key, e.target.value)}
                            sx={{ display: "flex", alignItems: "center", mt: 2 }}
                          >
                            <FormControlLabel
                              value="view"
                              control={<BpRadio size="small" />}
                              label={<Typography sx={{ fontSize: "12px", fontWeight: 500, ml: 1 }}>Can View</Typography>}
                              sx={{ m: 0 }}
                            />
                            <FormControlLabel
                              value="edit"
                              control={<BpRadio size="small" />}
                              label={<Typography sx={{ fontSize: "12px", fontWeight: 500, ml: 1 }}>Can Edit</Typography>}
                              sx={{ m: 0, ml: 5 }}
                            />
                          </RadioGroup>
                        </Box>
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt:2,
                        minWidth: "70px",
                        justifyContent: "flex-start",
                      }}
                    >
                      <AntSwitch
                        checked={moduleToggles[effectiveKey]?.[module.key] || false}
                        onChange={() => handleToggle(module.key)}
                      />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-start", mt: 4, pt: 2 }}>
        <LoadingButton
          variant="contained"
          onClick={onSave}
          loading={loading}
          disabled={!hasChanges}
          sx={{
            borderRadius: "50px",
            textTransform: "none",
            backgroundColor: hasChanges ? "#28AA4A" : "#B0B0B0",
            fontSize: "14px",
            fontWeight: 500,
            px: 4,
            "&:hover": { backgroundColor: hasChanges ? "#28AA4A" : "#B0B0B0" },
          }}
        >
          Save Changes
        </LoadingButton>

        <Button
          variant="contained"
          onClick={onCancel}
          sx={{
            borderRadius: "50px",
            textTransform: "none",
            backgroundColor: "#878787",
            fontSize: "14px",
            fontWeight: 500,
            px: 4,
            "&:hover": { backgroundColor: "#878787" },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SubAdminPermissionsComponent;